import { MessageKey } from '../i18n';
import { BikeConfig, DEFAULT_BIKE } from '../sim/config';
import { InputFrame } from '../sim/input';
import { rotate, vec } from '../sim/math';
import { FrameState } from './frame';
import { GameWorld } from './world';

export type DrillId = 'throttle' | 'brake' | 'figure8';
export type DrillStatus = 'idle' | 'running' | 'done';
export type Stars = 0 | 1 | 2 | 3;

export interface DrillResult {
  stars: Stars;
  /** Raw numbers for the result screen: time s, slip, wheelie s, distance m, speed km/h, stance %, missedCones. */
  metrics: Record<string, number>;
  /** Why stars were lost, most important first. */
  reasons: MessageKey[];
}

export type DrillEvent =
  | { type: 'started' }
  | { type: 'finished'; result: DrillResult }
  | { type: 'missedCone'; cone: number }
  | { type: 'wrongStance'; expected: 'standing' | 'seated' }
  | { type: 'hitCone' };

/** A drill is a state machine idle -> running -> done, fed one frame per physics step. It reads only sim state. */
export interface Drill {
  readonly id: DrillId;
  /** Start name on the ring level. */
  readonly start: string;
  readonly status: DrillStatus;
  evaluate(frame: FrameState): DrillEvent[];
  result(): DrillResult | null;
}

export abstract class BaseDrill implements Drill {
  abstract readonly id: DrillId;
  abstract readonly start: string;
  status: DrillStatus = 'idle';
  startTime = 0;
  /** Cones knocked over during the drill (the sim counts them; each costs a star, the drill still counts). */
  conesHit = 0;
  private conesAtStart = -1;
  private final: DrillResult | null = null;

  constructor(readonly cfg: BikeConfig = DEFAULT_BIKE) {}

  result(): DrillResult | null { return this.final; }

  evaluate(f: FrameState): DrillEvent[] {
    if (this.status === 'done') return [];
    const events: DrillEvent[] = [];
    if (this.status === 'idle') {
      if (!this.shouldStart(f)) return events;
      this.status = 'running';
      this.startTime = f.time - f.dt;
      events.push({ type: 'started' });
    }
    const knocked = f.bike.telemetry.conesHit ?? 0;
    if (this.conesAtStart < 0) this.conesAtStart = knocked;
    if (knocked - this.conesAtStart > this.conesHit) { this.conesHit = knocked - this.conesAtStart; events.push({ type: 'hitCone' }); }
    const failure: MessageKey | null = f.bike.telemetry.fallen ? 'result.reason.fallen'
      : f.time - this.startTime > this.timeLimit ? 'result.reason.timeout' : null;
    const outcome = failure ? this.fail(failure) : this.track(f, events);
    if (outcome && !failure && this.conesHit > 0) {
      outcome.stars = Math.max(0, outcome.stars - 1) as Stars;
      outcome.reasons.push('result.reason.hitCone');
      outcome.metrics.conesHit = this.conesHit;
    }
    if (outcome) {
      this.status = 'done';
      this.final = outcome;
      events.push({ type: 'finished', result: outcome });
    }
    return events;
  }

  protected fail(reason: MessageKey): DrillResult {
    return { stars: 0, metrics: this.metrics(), reasons: [reason] };
  }

  protected abstract readonly timeLimit: number;
  protected abstract shouldStart(f: FrameState): boolean;
  /** Per-frame bookkeeping; returns the result once the drill is over. */
  protected abstract track(f: FrameState, events: DrillEvent[]): DrillResult | null;
  protected abstract metrics(): Record<string, number>;
}

/** Front axle position in world space. */
export function frontAxle(f: FrameState, cfg: BikeConfig) {
  const b = f.bike;
  const off = rotate(b.orientation, vec(0, 0, cfg.wheelbase / 2));
  return { x: b.position.x + off.x, z: b.position.z + off.z };
}

export const round = (v: number, digits = 2) => Math.round(v * 10 ** digits) / 10 ** digits;

/** Steps a world through a drill with a controller until the drill is done or maxTime runs out. */
export function runDrill(drill: Drill, world: GameWorld, controller: (f: FrameState) => InputFrame, maxTime = 180) {
  const events: DrillEvent[] = [];
  let f = world.frame();
  while (drill.status !== 'done' && world.time < maxTime) {
    f = world.step(controller(f));
    events.push(...drill.evaluate(f));
  }
  return { result: drill.result(), events, world };
}
